import { HOME_TAB_OF_MODE, homeModeFromSearch } from './projectTabUrl';

/**
 * When the URL names a service and a tab (`/code/<tenant>/<project>/<service>/<branch>/<tab>`), the URL wins over
 * what the browser remembers (`mia_activeTab`, `mia_selectedResource`). A remembered Project Home / Dashboard used
 * to be restored over a link to a service's Code view: Canvas then wrote `?view=dashboard` into that service URL and,
 * reading it back as a Home link, switched to the Preview tab (…/code → …/code?view=dashboard → …/preview).
 */

/**
 * Whether a project's arriving URL lands on its Home (the dashboard): a project URL naming no service
 * (`/code/<tenant>/<project>`, with or without `?view=`), or any URL with a Home `?view=`. Opening a project never
 * picks a service to preview by itself (the project may be empty, or have no default service): the Preview renders
 * once the person opens the Preview tab or presses Start. A URL naming a service (`…/<service>/<branch>/code/<file>`,
 * `…/<service>/<branch>/preview`) keeps it.
 * @param {{ search: string, serviceInUrl: boolean }} input
 */
export function landsOnHome({ search, serviceInUrl }) {
  return !serviceInUrl || homeModeFromSearch(search) !== null;
}

/**
 * The tab Canvas opens on. A project URL naming no service opens on the Preview tab, where Home lives (landsOnHome:
 * never a remembered Code tab, which on Home was only its Files view); a URL naming a service keeps its own tab.
 * @param {{ search?: string, serviceInUrl: boolean, initialTab?: string|null, rememberedTab?: string|null }} input (`search` no longer matters)
 */
export function initialActiveTab({ serviceInUrl, initialTab = null, rememberedTab = null }) {
  if (!serviceInUrl) { return 'preview'; }
  return initialTab || rememberedTab || 'preview';
}

/**
 * The Home `?view=` Canvas keeps in the URL: the Home mode while at Home, never on a URL naming a service.
 * @param {{ atHome: boolean, serviceInUrl: boolean, mode: string|null }} input
 */
export function homeViewParam({ atHome, serviceInUrl, mode }) {
  return atHome && !serviceInUrl ? (mode || null) : null;
}

/**
 * Whether Home with the Code tab set should switch to the Preview tab (Home of a vfs project has no Code tab: it
 * showed the empty "Source Code" view). Never while the URL names a service: that Code tab is the service's.
 * @param {{ resourceType: string|null, activeTab: string, serviceInUrl: boolean, hasCodeOfItsOwn: boolean }} input
 */
export function homeLeavesCodeTab({ resourceType, activeTab, serviceInUrl, hasCodeOfItsOwn }) {
  return !serviceInUrl && resourceType === 'configuration' && activeTab === 'code' && !hasCodeOfItsOwn;
}

/**
 * The remembered selection to start from: none from another project, and with a service in the URL only that
 * very service (never a remembered Home over it).
 * @param {object|null} remembered `mia_selectedResource`, parsed
 * @param {{ projectIdFromUrl: string, serviceFromUrl: string|null }} url
 */
export function rememberedSelectionFor(remembered, { projectIdFromUrl, serviceFromUrl }) {
  if (!remembered || remembered.projectId !== projectIdFromUrl) { return null; }
  if (serviceFromUrl) { return remembered.type === 'service' && remembered.data?.name === serviceFromUrl ? remembered : null; }
  return remembered;
}

/**
 * Whether the browser's address already shows the Home `?view=` that Canvas wants, even though the router's own
 * location has not caught up yet (a button that navigates and sets the Home mode in one go: the mode lands first). The
 * URL sync must then leave it alone, or it rewrites the router's older query over the new one and drops what the
 * button just put in it (the Logs button's `&service=` fell away, and the Logs view opened on the first service).
 * @param {string} liveSearch the address bar's query (`window.location.search`)
 * @param {string|null} wanted the view Canvas wants in the URL
 */
export function addressAlreadyShows(liveSearch, wanted) {
  if (!wanted) { return false; }
  const params = new URLSearchParams(liveSearch || '');
  return !params.has('view') && params.get('tab') === HOME_TAB_OF_MODE[wanted];
}


/**
 * Whether picking a service in the scope dropdown should land on that service's Code tab: the person is on Code, either
 * the service-level Code tab or the Files view at Home (the header's Code tab there leaves `activeTab` at 'preview').
 * Without this the pick fell into the Preview branch: it started the preview and left the Code tab.
 * @param {{ activeTab: string, atHome: boolean, homeMode: string|null }} input
 */
export function servicePickKeepsCode({ activeTab, atHome, homeMode }) {
  return activeTab === 'code' || (atHome && homeMode === 'files');
}
